/**
 * ΠΡΟΤΥΠΑ ΚΕΝΤΡΙΚΟΥ ΚΑΤΑΛΟΓΟΥ — έτοιμα σετ μεγεθών, υλικών και ομάδων επιλογών του οργανισμού (org_size_templates,
 * org_ingredient_templates, org_option_group_templates). Η φόρμα προϊόντος του κεντρικού καταλόγου τα διαβάζει
 * («Μεγέθη από πρότυπο», «Ομάδα από πρότυπο») και οι ομάδες που ήρθαν από πρότυπο αλλάζουν ΚΕΝΤΡΙΚΑ από εδώ, με
 * «Εφαρμογή» στα συνδεδεμένα προϊόντα. ΔΕΝ είναι τα πρότυπα εκτύπωσης (print_templates) που έπαψαν να
 * διαβάζονται με το migration 272 — αυτά εδώ είναι ζωντανή λειτουργία του καταλόγου.
 *
 * Νέα εμφάνιση (14/10/2026): υπο-καρτέλες + «Πίνακας» (κλικ σε γραμμή = συρτάρι με τα στοιχεία και τις ενέργειες
 * του προτύπου), οι φόρμες σε συρτάρι, οι ερωτήσεις στο ConfirmModal αντί για window.confirm, τα ποσά με
 * formatMoney. Κλήσεις, σώματα αιτημάτων και κανόνες επικύρωσης ίδια με πριν.
 */
import React, { useState, useEffect, useCallback, useId, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { organizationAPI } from '../services/api';
import { Check, Copy, Loader2, Pencil, Plus, RefreshCw, Save, Trash2, X } from 'lucide-react';
import ConfirmModal from './shared/ConfirmModal';
import { formatMoney } from '../utils/formatMoney';
import { SheetField } from './settings/sheet';
import {
  ASIDE_BTN, Column, DANGER_GHOST_BTN, DataTable, DetailPanel, EmptyState, GHOST_BTN, ICON_BTN, INPUT_CLS, ListPage, ListToolbar,
  PRIMARY_BTN, SELECT_CLS, StatusChip, matchesSearch,
} from './shared/listPage';
import { Banner, COMPACT_INPUT, FlashMessage, FlashToast, GroupTitle, LINK_BTN, ROW_DANGER_BTN, SubTabs } from './orgPages/ui';

// ============================================================
// Types
// ============================================================

interface SizeTemplateItem {
  id?: number;
  name: string;
  price_modifier: number;
  is_default: boolean;
  is_available: boolean;
  price_modifier_take_away: number | null;
  price_modifier_delivery: number | null;
  price_modifier_dine_in: number | null;
}

interface SizeTemplate {
  id: number;
  organization_id: number;
  name: string;
  description: string | null;
  items: SizeTemplateItem[];
  linked_products: number;
}

interface IngredientTemplateItem {
  id?: number;
  ingredient_name: string;
  extra_price: number;
  is_default: boolean;
  category: string | null;
}

interface IngredientTemplate {
  id: number;
  organization_id: number;
  name: string;
  description: string | null;
  items: IngredientTemplateItem[];
  linked_products: number;
}

interface OptionTemplateChoice {
  id?: number;
  name: string;
  price_modifier: number;
  is_default: boolean;
}

interface OptionTemplate {
  id: number;
  organization_id: number;
  name: string;
  selection_type: 'single' | 'multiple';
  is_required: boolean;
  description: string | null;
  choices: OptionTemplateChoice[];
  linked_products?: number;
}

interface Props {
  orgId: number;
  canManage: boolean;
}

type TabId = 'sizes' | 'ingredients' | 'options';

/** Μία γραμμή του πίνακα, ίδια για τα τρία είδη (το συρτάρι ξαναβρίσκει το πρότυπο από το id). */
interface TemplateRow {
  id: number;
  name: string;
  description: string | null;
  linked: number;
  /** «3 μεγέθη», «Μονή · 4 επιλογές». */
  itemsLabel: string;
  /** Τα ονόματα των στοιχείων, για μια ματιά χωρίς άνοιγμα. */
  preview: string;
  required: boolean;
  search: string[];
}

/** Ερώτηση πριν από μια ενέργεια (ως 14/10 window.confirm): ίδιο κείμενο, η ενέργεια τρέχει μόνο με «ναι». */
interface Ask {
  title: string;
  message: string;
  confirmLabel: string;
  variant: 'danger' | 'warning';
  run: () => void;
}

// Τα στοιχεία έρχονται άλλοτε ως πίνακας κι άλλοτε ως JSON κείμενο (όπως πάντα).
const listOf = (value: any): any[] => (typeof value === 'string' ? JSON.parse(value) : value) || [];

/** Λέξεις ενός μεταφρασμένου κειμένου με έντονα (π.χ. «ονόματα/δομή», «όχι τιμές»). */
const emphasizeAll = (text: string, parts: string[]): React.ReactNode => {
  const out: React.ReactNode[] = [];
  let rest = text;
  let key = 0;
  for (const part of parts) {
    const at = part ? rest.indexOf(part) : -1;
    if (at < 0) continue;
    out.push(rest.slice(0, at), <strong key={key++} className="font-semibold text-t1">{part}</strong>);
    rest = rest.slice(at + part.length);
  }
  out.push(rest);
  return <>{out}</>;
};

// ============================================================
// Component
// ============================================================

const OrgTemplatesManager: React.FC<Props> = ({ orgId, canManage }) => {
  const { t } = useTranslation(['admin', 'common', 'orgPages']);
  const uid = useId();
  const [tab, setTab] = useState<TabId>('sizes');
  const [sizeTemplates, setSizeTemplates] = useState<SizeTemplate[]>([]);
  const [ingredientTemplates, setIngredientTemplates] = useState<IngredientTemplate[]>([]);
  const [optionTemplates, setOptionTemplates] = useState<OptionTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  // Η ΠΡΩΤΗ φόρτωση που αποτυγχάνει δείχνει σφάλμα στη θέση της λίστας· μια ανανέωση μετά από ενέργεια
  // που αποτυγχάνει κρατά τη λίστα και λέει «Αποτυχία φόρτωσης» στο μήνυμα, όπως πριν.
  const [ready, setReady] = useState(false);
  const loadedOnce = useRef(false);
  const [loadError, setLoadError] = useState<'error' | 'forbidden' | null>(null);
  const [toast, setToast] = useState<FlashMessage | null>(null);
  const toastTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const [search, setSearch] = useState('');
  const [viewing, setViewing] = useState<{ tab: TabId; id: number } | null>(null);
  const [ask, setAsk] = useState<Ask | null>(null);

  // Size template modal
  const [showSizeModal, setShowSizeModal] = useState(false);
  const [editingSizeTemplate, setEditingSizeTemplate] = useState<SizeTemplate | null>(null);
  const [sizeForm, setSizeForm] = useState<{ name: string; description: string; items: { name: string; price_modifier: string; is_default: boolean }[] }>({
    name: '', description: '', items: []
  });
  const [saving, setSaving] = useState(false);

  // Ingredient template modal
  const [showIngModal, setShowIngModal] = useState(false);
  const [editingIngTemplate, setEditingIngTemplate] = useState<IngredientTemplate | null>(null);
  const [ingForm, setIngForm] = useState<{ name: string; description: string; items: { ingredient_name: string; extra_price: string; is_default: boolean; category: string }[] }>({
    name: '', description: '', items: []
  });

  // Option template modal
  const [showOptModal, setShowOptModal] = useState(false);
  const [editingOptTemplate, setEditingOptTemplate] = useState<OptionTemplate | null>(null);
  const [optForm, setOptForm] = useState<{ name: string; description: string; selection_type: 'single' | 'multiple'; is_required: boolean; choices: { name: string; price_modifier: string; is_default: boolean }[] }>({
    name: '', description: '', selection_type: 'single', is_required: false, choices: []
  });

  // Apply option template → products
  const [products, setProducts] = useState<any[]>([]);
  const [applyTmpl, setApplyTmpl] = useState<OptionTemplate | null>(null);
  const [applySel, setApplySel] = useState<Set<number>>(new Set());
  const [applySearch, setApplySearch] = useState('');
  // Templatize from an existing product's option groups
  const [fromOpen, setFromOpen] = useState(false);
  const [fromProductId, setFromProductId] = useState<string>('');
  const [fromLinkAll, setFromLinkAll] = useState(true);
  const [fromBusy, setFromBusy] = useState<number | null>(null);
  // Size template from product (structure only)
  const [fromSizeOpen, setFromSizeOpen] = useState(false);
  const [fromSizeProductId, setFromSizeProductId] = useState<string>('');
  const [fromSizeName, setFromSizeName] = useState('');
  const [fromSizeBusy, setFromSizeBusy] = useState(false);

  // ============================================================
  // Data loading
  // ============================================================

  const showToast = useCallback((type: 'success' | 'error', message: string) => {
    clearTimeout(toastTimer.current);
    setToast({ type, message });
    toastTimer.current = setTimeout(() => setToast(null), 3000);
  }, []);
  useEffect(() => () => clearTimeout(toastTimer.current), []);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [sData, iData, oData, pData] = await Promise.all([
        organizationAPI.getSizeTemplates(orgId),
        organizationAPI.getIngredientTemplates(orgId),
        organizationAPI.getOptionTemplates(orgId).catch(() => ({ templates: [] })),
        organizationAPI.getCatalogProducts(orgId).catch(() => ({ products: [] })),
      ]);
      setSizeTemplates(sData.templates || []);
      setIngredientTemplates(iData.templates || []);
      setOptionTemplates(oData.templates || []);
      setProducts(pData.products || []);
      loadedOnce.current = true;
      setReady(true);
      setLoadError(null);
    } catch (err: any) {
      console.error('Failed to load templates:', err);
      if (loadedOnce.current) showToast('error', t('admin:failedLoadTemplates'));
      else setLoadError(err?.response?.status === 403 ? 'forbidden' : 'error');
    } finally {
      setLoading(false);
    }
  }, [orgId, t, showToast]);

  useEffect(() => { loadData(); }, [loadData]);

  // ============================================================
  // Size Template Handlers
  // ============================================================

  const openSizeModal = (template?: SizeTemplate) => {
    if (template) {
      setEditingSizeTemplate(template);
      const items = (typeof template.items === 'string' ? JSON.parse(template.items) : template.items) || [];
      setSizeForm({
        name: template.name,
        description: template.description || '',
        items: items.map((i: SizeTemplateItem) => ({
          name: i.name, price_modifier: String(i.price_modifier || 0), is_default: i.is_default
        }))
      });
    } else {
      setEditingSizeTemplate(null);
      setSizeForm({ name: '', description: '', items: [{ name: '', price_modifier: '0', is_default: true }] });
    }
    setShowSizeModal(true);
  };

  const handleSaveSizeTemplate = async () => {
    if (!sizeForm.name.trim() || sizeForm.items.length === 0) return;
    try {
      setSaving(true);
      const data = {
        name: sizeForm.name.trim(),
        description: sizeForm.description || null,
        items: sizeForm.items.filter(i => i.name.trim()).map(i => ({
          name: i.name.trim(),
          price_modifier: parseFloat(i.price_modifier) || 0,
          is_default: i.is_default
        }))
      };
      if (editingSizeTemplate) {
        await organizationAPI.updateSizeTemplate(orgId, editingSizeTemplate.id, data);
        showToast('success', t('admin:sizeTemplateUpdated'));
      } else {
        await organizationAPI.createSizeTemplate(orgId, data);
        showToast('success', t('admin:sizeTemplateCreated'));
      }
      setShowSizeModal(false);
      await loadData();
    } catch (err: any) {
      showToast('error', err.response?.data?.error || t('admin:failedSave2'));
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteSizeTemplate = (tmpl: SizeTemplate) => {
    const warning = tmpl.linked_products > 0 ? t('admin:linkedProductsWarning', { count: tmpl.linked_products }) : '';
    setAsk({
      title: t('orgPages:templates.deleteTitle'),
      message: t('admin:deleteSizeTemplateConfirm', { name: tmpl.name, warning }),
      confirmLabel: t('common:delete'),
      variant: 'danger',
      run: async () => {
        try {
          await organizationAPI.deleteSizeTemplate(orgId, tmpl.id);
          showToast('success', t('admin:templateDeleted'));
          await loadData();
        } catch (err: any) {
          showToast('error', err.response?.data?.error || t('admin:failedDelete2'));
        }
      },
    });
  };

  const handleReapplySize = (tmpl: SizeTemplate) => {
    setAsk({
      title: t('orgPages:templates.reapplyTitle'),
      message: t('admin:reapplySizeConfirm', { name: tmpl.name, count: tmpl.linked_products }),
      confirmLabel: t('orgPages:templates.apply'),
      variant: 'warning',
      run: async () => {
        try {
          const result = await organizationAPI.reapplySizeTemplate(orgId, tmpl.id);
          showToast('success', result.message);
          await loadData();
        } catch (err: any) {
          showToast('error', err.response?.data?.error || t('common:somethingWentWrong'));
        }
      },
    });
  };

  // ============================================================
  // Ingredient Template Handlers
  // ============================================================

  const openIngModal = (template?: IngredientTemplate) => {
    if (template) {
      setEditingIngTemplate(template);
      const items = (typeof template.items === 'string' ? JSON.parse(template.items) : template.items) || [];
      setIngForm({
        name: template.name,
        description: template.description || '',
        items: items.map((i: IngredientTemplateItem) => ({
          ingredient_name: i.ingredient_name, extra_price: String(i.extra_price || 0),
          is_default: i.is_default, category: i.category || ''
        }))
      });
    } else {
      setEditingIngTemplate(null);
      setIngForm({ name: '', description: '', items: [{ ingredient_name: '', extra_price: '0', is_default: true, category: '' }] });
    }
    setShowIngModal(true);
  };

  const handleSaveIngTemplate = async () => {
    if (!ingForm.name.trim() || ingForm.items.length === 0) return;
    try {
      setSaving(true);
      const data = {
        name: ingForm.name.trim(),
        description: ingForm.description || null,
        items: ingForm.items.filter(i => i.ingredient_name.trim()).map(i => ({
          ingredient_name: i.ingredient_name.trim(),
          extra_price: parseFloat(i.extra_price) || 0,
          is_default: i.is_default,
          category: i.category || null
        }))
      };
      if (editingIngTemplate) {
        await organizationAPI.updateIngredientTemplate(orgId, editingIngTemplate.id, data);
        showToast('success', t('admin:ingTemplateUpdated'));
      } else {
        await organizationAPI.createIngredientTemplate(orgId, data);
        showToast('success', t('admin:ingTemplateCreated'));
      }
      setShowIngModal(false);
      await loadData();
    } catch (err: any) {
      showToast('error', err.response?.data?.error || t('admin:failedSave2'));
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteIngTemplate = (tmpl: IngredientTemplate) => {
    const warning = tmpl.linked_products > 0 ? t('admin:linkedProductsWarning', { count: tmpl.linked_products }) : '';
    setAsk({
      title: t('orgPages:templates.deleteTitle'),
      message: t('admin:deleteIngTemplateConfirm', { name: tmpl.name, warning }),
      confirmLabel: t('common:delete'),
      variant: 'danger',
      run: async () => {
        try {
          await organizationAPI.deleteIngredientTemplate(orgId, tmpl.id);
          showToast('success', t('admin:templateDeleted'));
          await loadData();
        } catch (err: any) {
          showToast('error', err.response?.data?.error || t('admin:failedDelete2'));
        }
      },
    });
  };

  const handleReapplyIng = (tmpl: IngredientTemplate) => {
    setAsk({
      title: t('orgPages:templates.reapplyTitle'),
      message: t('admin:reapplyIngConfirm', { name: tmpl.name, count: tmpl.linked_products }),
      confirmLabel: t('orgPages:templates.apply'),
      variant: 'warning',
      run: async () => {
        try {
          const result = await organizationAPI.reapplyIngredientTemplate(orgId, tmpl.id);
          showToast('success', result.message);
          await loadData();
        } catch (err: any) {
          showToast('error', err.response?.data?.error || t('common:somethingWentWrong'));
        }
      },
    });
  };

  // ============================================================
  // Option Template Handlers
  // ============================================================

  const handleReapplyOption = (tmpl: OptionTemplate) => {
    setAsk({
      title: t('orgPages:templates.reapplyTitle'),
      message: t('orgPages:templates.options.reapplyConfirm', { name: tmpl.name, count: tmpl.linked_products || 0 }),
      confirmLabel: t('orgPages:templates.apply'),
      variant: 'warning',
      run: async () => {
        try {
          const result = await organizationAPI.reapplyOptionTemplate(orgId, tmpl.id);
          showToast('success', result.message || t('orgPages:templates.applied'));
          await loadData();
        } catch (err: any) {
          showToast('error', err.response?.data?.error || t('common:somethingWentWrong'));
        }
      },
    });
  };

  const submitApply = async () => {
    if (!applyTmpl || applySel.size === 0) return;
    try {
      const result = await organizationAPI.applyOptionTemplate(orgId, applyTmpl.id, Array.from(applySel));
      setApplyTmpl(null);
      await loadData();
      showToast('success', result.message || t('orgPages:templates.applyPanel.added'));
    } catch (err: any) {
      showToast('error', err.response?.data?.error || t('common:somethingWentWrong'));
    }
  };

  const templatizeFrom = async (groupId: number) => {
    try {
      setFromBusy(groupId);
      const result = await organizationAPI.templatizeOptionGroup(orgId, groupId, fromLinkAll);
      await loadData();
      showToast('success', result.message || t('orgPages:templates.created'));
    } catch (err: any) {
      showToast('error', err.response?.data?.error || t('common:somethingWentWrong'));
    } finally {
      setFromBusy(null);
    }
  };

  const templatizeAll = () => {
    setAsk({
      title: t('orgPages:templates.fromPanel.allTitle'),
      message: t('orgPages:templates.fromPanel.allConfirm'),
      confirmLabel: t('common:create'),
      variant: 'warning',
      run: async () => {
        try {
          setFromBusy(-1);
          const result = await organizationAPI.templatizeAllOptionGroups(orgId);
          await loadData();
          showToast('success', result.message || t('orgPages:templates.done'));
          setFromOpen(false);
        } catch (err: any) {
          showToast('error', err.response?.data?.error || t('common:somethingWentWrong'));
        } finally {
          setFromBusy(null);
        }
      },
    });
  };

  const createSizeFromProduct = async () => {
    if (!fromSizeProductId) return;
    try {
      setFromSizeBusy(true);
      const result = await organizationAPI.createSizeTemplateFromProduct(orgId, Number(fromSizeProductId), fromSizeName.trim() || undefined);
      setFromSizeOpen(false);
      await loadData();
      showToast('success', result.message || t('orgPages:templates.created'));
    } catch (err: any) {
      showToast('error', err.response?.data?.error || t('common:somethingWentWrong'));
    } finally {
      setFromSizeBusy(false);
    }
  };

  const openOptModal = (template?: OptionTemplate) => {
    if (template) {
      setEditingOptTemplate(template);
      const choices = (typeof template.choices === 'string' ? JSON.parse(template.choices) : template.choices) || [];
      setOptForm({
        name: template.name,
        description: template.description || '',
        selection_type: template.selection_type === 'multiple' ? 'multiple' : 'single',
        is_required: template.is_required === true,
        choices: choices.map((c: OptionTemplateChoice) => ({
          name: c.name, price_modifier: String(c.price_modifier || 0), is_default: c.is_default
        }))
      });
    } else {
      setEditingOptTemplate(null);
      setOptForm({ name: '', description: '', selection_type: 'single', is_required: false, choices: [{ name: '', price_modifier: '0', is_default: false }] });
    }
    setShowOptModal(true);
  };

  const handleSaveOptTemplate = async () => {
    if (!optForm.name.trim() || optForm.choices.filter(c => c.name.trim()).length === 0) return;
    try {
      setSaving(true);
      const data = {
        name: optForm.name.trim(),
        description: optForm.description || null,
        selection_type: optForm.selection_type,
        is_required: optForm.is_required,
        choices: optForm.choices.filter(c => c.name.trim()).map(c => ({
          name: c.name.trim(),
          price_modifier: parseFloat(c.price_modifier) || 0,
          is_default: c.is_default
        }))
      };
      if (editingOptTemplate) {
        await organizationAPI.updateOptionTemplate(orgId, editingOptTemplate.id, data);
        showToast('success', t('orgPages:templates.options.updated'));
      } else {
        await organizationAPI.createOptionTemplate(orgId, data);
        showToast('success', t('orgPages:templates.options.createdToast'));
      }
      setShowOptModal(false);
      await loadData();
    } catch (err: any) {
      showToast('error', err.response?.data?.error || t('admin:failedSave2'));
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteOptTemplate = (tmpl: OptionTemplate) => {
    setAsk({
      title: t('orgPages:templates.deleteTitle'),
      message: t('orgPages:templates.options.deleteConfirm', { name: tmpl.name }),
      confirmLabel: t('common:delete'),
      variant: 'danger',
      run: async () => {
        try {
          await organizationAPI.deleteOptionTemplate(orgId, tmpl.id);
          showToast('success', t('admin:templateDeleted'));
          await loadData();
        } catch (err: any) {
          showToast('error', err.response?.data?.error || t('admin:failedDelete2'));
        }
      },
    });
  };

  // Toggling a single-select default is exclusive within the choice list.
  const updateOptChoice = (idx: number, field: string, value: any) => {
    setOptForm(f => {
      let choices = f.choices.map((c, i) => i === idx ? { ...c, [field]: value } : c);
      if (field === 'is_default' && value === true && f.selection_type === 'single') {
        choices = choices.map((c, i) => i === idx ? c : { ...c, is_default: false });
      }
      return { ...f, choices };
    });
  };

  // ============================================================
  // Γραμμές πίνακα
  // ============================================================

  const typeLabel = useCallback((type: string) => (type === 'multiple'
    ? t('orgPages:templates.options.multiple')
    : t('orgPages:templates.options.single')), [t]);

  const rowsByTab = useMemo<Record<TabId, TemplateRow[]>>(() => ({
    sizes: sizeTemplates.map(tmpl => {
      const items: SizeTemplateItem[] = listOf(tmpl.items);
      return {
        id: tmpl.id, name: tmpl.name, description: tmpl.description, linked: tmpl.linked_products || 0, required: false,
        itemsLabel: t('admin:templateSizeCount', { count: items.length }),
        preview: items.map(i => i.name).join(' · '),
        search: items.map(i => i.name),
      };
    }),
    ingredients: ingredientTemplates.map(tmpl => {
      const items: IngredientTemplateItem[] = listOf(tmpl.items);
      return {
        id: tmpl.id, name: tmpl.name, description: tmpl.description, linked: tmpl.linked_products || 0, required: false,
        itemsLabel: t('admin:ingredientCount', { count: items.length }),
        preview: items.map(i => i.ingredient_name).join(' · '),
        search: items.map(i => i.ingredient_name),
      };
    }),
    options: optionTemplates.map(tmpl => {
      const choices: OptionTemplateChoice[] = listOf(tmpl.choices);
      return {
        id: tmpl.id, name: tmpl.name, description: tmpl.description, linked: tmpl.linked_products ?? 0, required: !!tmpl.is_required,
        itemsLabel: `${typeLabel(tmpl.selection_type)} · ${t('orgPages:templates.options.choices', { count: choices.length })}`,
        preview: choices.map(c => c.name).join(' · '),
        search: choices.map(c => c.name),
      };
    }),
  }), [sizeTemplates, ingredientTemplates, optionTemplates, t, typeLabel]);

  const allRows = rowsByTab[tab];
  const rows = useMemo(
    () => allRows.filter(r => matchesSearch(search, r.name, r.description, ...r.search)),
    [allRows, search],
  );

  const columns = useMemo<Column<TemplateRow>[]>(() => [
    {
      key: 'name',
      header: t('orgPages:templates.columns.template'),
      width: 'minmax(180px, 1.3fr)',
      render: r => (
        <div className="min-w-0">
          <p className="truncate font-medium text-t1">{r.name}</p>
          {r.description && <p className="truncate text-[12.5px] text-t3">{r.description}</p>}
        </div>
      ),
      stackRender: r => r.name,
    },
    {
      key: 'items',
      header: t('orgPages:templates.columns.items'),
      width: 'minmax(200px, 2fr)',
      render: r => (
        <div className="min-w-0">
          <p className="flex flex-wrap items-center gap-x-2 gap-y-1 text-t2">
            <span>{r.itemsLabel}</span>
            {r.required && <StatusChip tone="muted">{t('orgPages:templates.options.required')}</StatusChip>}
          </p>
          {r.preview && <p className="truncate text-[12.5px] text-t3">{r.preview}</p>}
        </div>
      ),
      stackRender: r => r.itemsLabel,
    },
    {
      key: 'linked',
      header: t('orgPages:templates.columns.products'),
      width: '120px',
      align: 'right',
      render: r => (
        <span className={`tabular-nums ${r.linked > 0 ? 'text-t2' : 'text-t4'}`}>
          {t('orgPages:templates.products', { count: r.linked })}
        </span>
      ),
    },
  ], [t]);

  // ============================================================
  // Συρτάρι προτύπου (στοιχεία + ενέργειες)
  // ============================================================

  const viewSize = viewing?.tab === 'sizes' ? sizeTemplates.find(x => x.id === viewing.id) : undefined;
  const viewIng = viewing?.tab === 'ingredients' ? ingredientTemplates.find(x => x.id === viewing.id) : undefined;
  const viewOpt = viewing?.tab === 'options' ? optionTemplates.find(x => x.id === viewing.id) : undefined;
  const viewTmpl: SizeTemplate | IngredientTemplate | OptionTemplate | undefined = viewSize || viewIng || viewOpt;
  const viewRow = viewing && viewTmpl ? rowsByTab[viewing.tab].find(r => r.id === viewTmpl.id) : undefined;

  const check = (on: boolean) => (on ? (
    <>
      <Check className="mx-auto h-4 w-4 text-brand" aria-hidden="true" />
      <span className="sr-only">{t('admin:defaultHeader')}</span>
    </>
  ) : null);

  // Στο συρτάρι (στενό) οι πίνακες γίνονται στοίβα: όνομα + ποσό δεξιά, από κάτω «Προεπιλογή» / κατηγορία.
  const defaultText = (on: boolean) => (on ? t('admin:defaultHeader') : null);
  const sizeItemColumns: Column<SizeTemplateItem>[] = [
    { key: 'name', header: t('admin:sizeHeader'), stack: 'title', render: i => <span className="text-t1">{i.name}</span> },
    // Ε10α: τελική τιμή μεγέθους (η εφαρμογή του προτύπου τη γράφει αυτούσια), όχι «+x»
    { key: 'price', header: t('common:price'), width: '120px', align: 'right', stack: 'end', render: i => <span className="tabular-nums text-t2">{formatMoney(i.price_modifier)}</span> },
    { key: 'default', header: t('admin:defaultHeader'), width: '110px', align: 'center', stack: 'meta', render: i => check(i.is_default), stackRender: i => defaultText(i.is_default) },
  ];
  const ingItemColumns: Column<IngredientTemplateItem>[] = [
    { key: 'name', header: t('admin:ingredientHeader'), stack: 'title', render: i => <span className="text-t1">{i.ingredient_name}</span> },
    {
      key: 'extra', header: t('admin:extraHeader'), width: '110px', align: 'right', stack: 'end',
      render: i => <span className="tabular-nums text-t2">{i.extra_price > 0 ? `+${formatMoney(i.extra_price)}` : '—'}</span>,
    },
    { key: 'default', header: t('admin:defaultHeader'), width: '110px', align: 'center', stack: 'meta', render: i => check(i.is_default), stackRender: i => defaultText(i.is_default) },
    { key: 'category', header: t('admin:categoryHeader'), width: '130px', stack: 'meta', render: i => <span className="text-t3">{i.category || '—'}</span>, stackRender: i => i.category || null },
  ];
  const optChoiceColumns: Column<OptionTemplateChoice>[] = [
    { key: 'name', header: t('orgPages:templates.options.choice'), stack: 'title', render: c => <span className="text-t1">{c.name}</span> },
    {
      key: 'price', header: t('common:price'), width: '120px', align: 'right', stack: 'end',
      render: c => <span className="tabular-nums text-t2">{c.price_modifier > 0 ? `+${formatMoney(c.price_modifier)}` : formatMoney(c.price_modifier)}</span>,
    },
    { key: 'default', header: t('admin:defaultHeader'), width: '110px', align: 'center', stack: 'meta', render: c => check(c.is_default), stackRender: c => defaultText(c.is_default) },
  ];

  const closeView = () => {
    // Ερώτηση ανοιχτή πάνω από το συρτάρι: το Esc κλείνει πρώτα εκείνη.
    if (ask) { setAsk(null); return; }
    setViewing(null);
  };

  const viewBody = () => {
    if (!viewTmpl) return null;
    // Ένας πίνακας ανά είδος· κλειδί γραμμής = η θέση της στον ΙΔΙΟ πίνακα (τα στοιχεία δεν έχουν πάντα id).
    let table: React.ReactNode = null;
    if (viewSize) {
      const items: SizeTemplateItem[] = listOf(viewSize.items);
      table = (
        <DataTable columns={sizeItemColumns} rows={items} rowKey={i => items.indexOf(i)}
          dense ariaLabel={t('orgPages:templates.tabs.sizes')} empty={t('orgPages:templates.noItems')} testId="template-items" />
      );
    } else if (viewIng) {
      const items: IngredientTemplateItem[] = listOf(viewIng.items);
      table = (
        <DataTable columns={ingItemColumns} rows={items} rowKey={i => items.indexOf(i)}
          dense ariaLabel={t('orgPages:templates.tabs.ingredients')} empty={t('orgPages:templates.noItems')} testId="template-items" />
      );
    } else if (viewOpt) {
      const choices: OptionTemplateChoice[] = listOf(viewOpt.choices);
      table = (
        <DataTable columns={optChoiceColumns} rows={choices} rowKey={c => choices.indexOf(c)}
          dense ariaLabel={t('orgPages:templates.tabs.options')} empty={t('orgPages:templates.noItems')} testId="template-items" />
      );
    }
    const linked = viewRow?.linked || 0;
    const reapplyLabel = viewOpt ? t('orgPages:templates.options.reapply') : t('admin:reapplyTitle');
    return (
      <div className="space-y-5">
        {viewOpt && (
          <p className="flex flex-wrap items-center gap-2 text-sm text-t2">
            <span>{typeLabel(viewOpt.selection_type)}</span>
            {viewOpt.is_required && <StatusChip tone="muted">{t('orgPages:templates.options.required')}</StatusChip>}
          </p>
        )}
        {table}
        {/* Τα συνδεδεμένα προϊόντα και ό,τι τα αφορά (εφαρμογή / προσθήκη) μαζί, όχι στο κάτω μέρος. */}
        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-line pt-4" data-testid="template-linked">
          <p className="text-sm text-t3">
            {linked > 0 ? t('orgPages:templates.linked', { count: linked }) : t('orgPages:templates.notLinked')}
          </p>
          {canManage && (linked > 0 || !!viewOpt) && (
            <div className="flex flex-wrap gap-2">
              {linked > 0 && (
                <button type="button" onClick={viewReapply} className={ASIDE_BTN} title={reapplyLabel}>
                  <RefreshCw className="h-4 w-4" aria-hidden="true" />
                  {reapplyLabel}
                </button>
              )}
              {viewOpt && (
                <button type="button" className={ASIDE_BTN}
                  onClick={() => { const tmpl = viewOpt; setViewing(null); setApplyTmpl(tmpl); setApplySel(new Set()); setApplySearch(''); }}>
                  <Plus className="h-4 w-4" aria-hidden="true" />
                  {t('orgPages:templates.options.addToProducts')}
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  const viewReapply = () => {
    if (viewSize) handleReapplySize(viewSize);
    else if (viewIng) handleReapplyIng(viewIng);
    else if (viewOpt) handleReapplyOption(viewOpt);
  };

  const viewFooter = () => {
    if (!viewTmpl) return null;
    if (!canManage) {
      return <button type="button" onClick={() => setViewing(null)} className={GHOST_BTN}>{t('common:close')}</button>;
    }
    const edit = () => {
      setViewing(null);
      if (viewSize) openSizeModal(viewSize);
      else if (viewIng) openIngModal(viewIng);
      else if (viewOpt) openOptModal(viewOpt);
    };
    return (
      <button type="button" onClick={edit} className={PRIMARY_BTN}>
        <Pencil className="h-4 w-4" aria-hidden="true" />
        {t('common:edit')}
      </button>
    );
  };

  const viewDelete = () => {
    if (viewSize) handleDeleteSizeTemplate(viewSize);
    else if (viewIng) handleDeleteIngTemplate(viewIng);
    else if (viewOpt) handleDeleteOptTemplate(viewOpt);
  };

  // ============================================================
  // Render
  // ============================================================

  const tabs = [
    { id: 'sizes' as const, label: t('orgPages:templates.tabs.sizes'), count: ready ? sizeTemplates.length : null, testId: 'templates-tab-sizes' },
    { id: 'ingredients' as const, label: t('orgPages:templates.tabs.ingredients'), count: ready ? ingredientTemplates.length : null, testId: 'templates-tab-ingredients' },
    { id: 'options' as const, label: t('orgPages:templates.tabs.options'), count: ready ? optionTemplates.length : null, testId: 'templates-tab-options' },
  ];

  const emptyOf = (): React.ReactNode => {
    if (allRows.length > 0) return <EmptyState title={t('orgPages:templates.noMatches')} />;
    if (tab === 'sizes') return <EmptyState title={t('admin:noSizeTemplates')} hint={t('admin:noSizeTemplatesHint')} />;
    if (tab === 'ingredients') return <EmptyState title={t('admin:noIngredientTemplates')} hint={t('admin:noIngredientTemplatesHint')} />;
    return <EmptyState title={t('orgPages:templates.options.empty')} hint={t('orgPages:templates.options.emptyHint')} />;
  };

  const applyFilter = (p: any) => !applySearch || p.name.toLowerCase().includes(applySearch.toLowerCase());
  const applyHas = (p: any) => !!applyTmpl && (p.option_groups || []).some((g: any) => g.template_id === applyTmpl.id);

  const saveBtn = (onClick: () => void, disabled: boolean, editing: boolean) => (
    <button type="button" onClick={onClick} disabled={disabled} className={PRIMARY_BTN}>
      {saving ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : <Save className="h-4 w-4" aria-hidden="true" />}
      {editing ? t('common:save') : t('common:create')}
    </button>
  );

  const defaultLabel = 'inline-flex min-h-[36px] cursor-pointer select-none items-center gap-1.5 whitespace-nowrap text-[13px] text-t3';
  const checkCls = 'h-4 w-4 shrink-0 accent-[hsl(var(--brand))]';

  return (
    <ListPage embedded title={t('admin:templatesTabLabel')} testId="org-templates">
      <FlashToast toast={toast} />

      <p className="pb-3 text-sm text-t3">{t('orgPages:templates.subtitle')}</p>

      <div className="pb-3">
        <SubTabs<TabId>
          tabs={tabs}
          value={tab}
          onChange={id => { setTab(id); setSearch(''); }}
          label={t('orgPages:templates.tabsLabel')}
          testId="templates-tabs"
        />
      </div>

      {loadError ? (
        <Banner tone="danger" role="alert" testId="templates-load-error" action={(
          <button type="button" onClick={() => { setLoadError(null); loadData(); }} className={GHOST_BTN}>
            <RefreshCw className="h-4 w-4" aria-hidden="true" />
            {t('common:tryAgain')}
          </button>
        )}>
          {loadError === 'forbidden' ? t('orgPages:common.forbidden') : t('admin:failedLoadTemplates')}
        </Banner>
      ) : (
        <>
          <ListToolbar
            search={{ value: search, onChange: setSearch, placeholder: t('orgPages:templates.search') }}
            actions={(
              <>
                <button type="button" onClick={loadData} disabled={loading} className={ICON_BTN}
                  aria-label={t('orgPages:common.refresh')} title={t('orgPages:common.refresh')}>
                  <RefreshCw className={`h-4 w-4 ${loading && ready ? 'animate-spin' : ''}`} aria-hidden="true" />
                </button>
                {canManage && (tab === 'options' || tab === 'sizes') && (
                  <button
                    type="button"
                    onClick={() => (tab === 'sizes'
                      ? (setFromSizeProductId(''), setFromSizeName(''), setFromSizeOpen(true))
                      : (setFromProductId(''), setFromOpen(true)))}
                    className={GHOST_BTN}
                    title={t('orgPages:templates.fromProductHint')}
                  >
                    <Copy className="h-4 w-4" aria-hidden="true" />
                    {t('orgPages:templates.fromProduct')}
                  </button>
                )}
                {canManage && (
                  <button
                    type="button"
                    onClick={() => (tab === 'sizes' ? openSizeModal() : tab === 'ingredients' ? openIngModal() : openOptModal())}
                    className={PRIMARY_BTN}
                  >
                    <Plus className="h-4 w-4" aria-hidden="true" />
                    {t('admin:newTemplate')}
                  </button>
                )}
              </>
            )}
            summary={ready ? (search.trim()
              ? t('orgPages:templates.shown', { shown: rows.length, total: allRows.length })
              : t('orgPages:templates.count', { count: allRows.length })) : null}
          />

          <DataTable
            columns={columns}
            rows={rows}
            rowKey={r => r.id}
            onRowClick={r => setViewing({ tab, id: r.id })}
            selectedKey={viewing && viewing.tab === tab ? viewing.id : null}
            loading={loading}
            rowLabel={r => t('orgPages:templates.rowLabel', { name: r.name })}
            empty={emptyOf()}
            testId="templates-table"
          />
        </>
      )}

      {/* Το πρότυπο: στοιχεία + ενέργειες */}
      <DetailPanel
        open={!!viewTmpl}
        onClose={closeView}
        title={viewTmpl?.name || ''}
        subtitle={viewRow ? [viewRow.itemsLabel, viewRow.description].filter(Boolean).join(' · ') : undefined}
        footer={viewFooter()}
        footerStart={canManage && viewTmpl ? (
          <button type="button" onClick={viewDelete} className={DANGER_GHOST_BTN}>
            <Trash2 className="h-4 w-4" aria-hidden="true" />
            {t('common:delete')}
          </button>
        ) : undefined}
        testId="template-panel"
      >
        {viewBody()}
      </DetailPanel>

      {/* Apply option template → products */}
      <DetailPanel
        open={!!applyTmpl}
        onClose={() => setApplyTmpl(null)}
        title={applyTmpl ? t('orgPages:templates.applyPanel.title', { name: applyTmpl.name }) : ''}
        footer={(
          <>
            <button type="button" onClick={() => setApplyTmpl(null)} className={GHOST_BTN}>{t('common:cancel')}</button>
            <button type="button" onClick={submitApply} disabled={applySel.size === 0} className={PRIMARY_BTN}>
              {t('orgPages:templates.applyPanel.submit', { count: applySel.size })}
            </button>
          </>
        )}
        testId="template-apply-panel"
      >
        {applyTmpl && (
          <div className="space-y-3">
            <input value={applySearch} onChange={e => setApplySearch(e.target.value)} placeholder={t('orgPages:templates.applyPanel.search')}
              aria-label={t('orgPages:templates.applyPanel.search')} className={INPUT_CLS} />
            <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1 text-[13px] text-t3">
              <span>
                {t('orgPages:templates.applyPanel.selected', { count: applySel.size })}
                {(() => {
                  const n = products.filter((p: any) => (p.option_groups || []).some((g: any) => g.template_id === applyTmpl.id)).length;
                  return n ? ` · ${t('orgPages:templates.applyPanel.haveIt', { count: n })}` : '';
                })()}
              </span>
              <span className="flex items-center gap-4">
                <button type="button" className={LINK_BTN}
                  onClick={() => setApplySel(new Set(products.filter(p => applyFilter(p) && !applyHas(p)).map(p => p.id)))}>
                  {t('orgPages:templates.applyPanel.missing')}
                </button>
                <button type="button" className={LINK_BTN}
                  onClick={() => setApplySel(new Set(products.filter(p => applyFilter(p)).map(p => p.id)))}>
                  {t('orgPages:templates.applyPanel.selectAll')}
                </button>
              </span>
            </div>
            <div className="divide-y divide-line border-y border-line">
              {products.filter(applyFilter).map(p => (
                <label key={p.id} className="flex min-h-[44px] cursor-pointer items-center gap-3 px-1 py-1.5 hover:bg-surface-2">
                  <input type="checkbox" checked={applySel.has(p.id)} className={checkCls}
                    onChange={e => setApplySel(prev => { const n = new Set(prev); e.target.checked ? n.add(p.id) : n.delete(p.id); return n; })} />
                  <span className="min-w-0 flex-1 truncate text-sm text-t1">{p.name}</span>
                  {applyHas(p) && <StatusChip tone="ok">{t('orgPages:templates.applyPanel.already')}</StatusChip>}
                </label>
              ))}
              {products.length === 0 && <p className="py-4 text-center text-sm text-t4">{t('orgPages:templates.applyPanel.noProducts')}</p>}
            </div>
          </div>
        )}
      </DetailPanel>

      {/* Templatize from an existing product's option groups */}
      <DetailPanel
        open={fromOpen}
        onClose={() => (ask ? setAsk(null) : setFromOpen(false))}
        title={t('orgPages:templates.fromPanel.title')}
        footer={<button type="button" onClick={() => setFromOpen(false)} className={GHOST_BTN}>{t('common:close')}</button>}
        testId="template-from-panel"
      >
        <div className="space-y-4">
          <p className="text-sm text-t3">{t('orgPages:templates.fromPanel.intro')}</p>
          <button type="button" onClick={templatizeAll} disabled={fromBusy !== null} className={`${PRIMARY_BTN} w-full`}>
            {fromBusy === -1 ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : <Copy className="h-4 w-4" aria-hidden="true" />}
            {t('orgPages:templates.fromPanel.all')}
          </button>
          <p className="text-center text-xs text-t4">{t('orgPages:templates.fromPanel.or')}</p>
          <SheetField label={t('orgPages:templates.fromPanel.product')} htmlFor={`${uid}-from-product`}>
            <select id={`${uid}-from-product`} value={fromProductId} onChange={e => setFromProductId(e.target.value)} className={SELECT_CLS}>
              <option value="">{t('orgPages:templates.fromPanel.pick')}</option>
              {products.filter(p => (p.option_groups || []).length > 0).map(p => (
                <option key={p.id} value={p.id}>{t('orgPages:templates.fromPanel.productGroups', { name: p.name, count: (p.option_groups || []).length })}</option>
              ))}
            </select>
          </SheetField>
          <label className="flex cursor-pointer select-none items-start gap-2.5 text-sm text-t2">
            <input type="checkbox" checked={fromLinkAll} onChange={e => setFromLinkAll(e.target.checked)} className={`${checkCls} mt-0.5`} />
            {t('orgPages:templates.fromPanel.linkAll')}
          </label>
          {fromProductId && (() => {
            const groups = ((products.find(p => String(p.id) === fromProductId) || {}).option_groups) || [];
            return (
              <div className="divide-y divide-line border-y border-line">
                {groups.map((g: any) => (
                  <div key={g.id} className="flex items-center justify-between gap-3 px-1 py-2.5">
                    <div className="min-w-0">
                      <p className="truncate text-sm font-medium text-t1">{g.name}</p>
                      <p className="text-[12.5px] text-t3">
                        {t('orgPages:templates.fromPanel.groupMeta', {
                          choices: t('orgPages:templates.options.choices', { count: (g.choices || []).length }),
                          type: typeLabel(g.selection_type),
                        })}
                        {g.template_id ? ` · ${t('orgPages:templates.fromPanel.alreadyLinked')}` : ''}
                      </p>
                    </div>
                    <button type="button" onClick={() => templatizeFrom(g.id)} disabled={fromBusy !== null || !!g.template_id} className={ASIDE_BTN}>
                      {fromBusy === g.id ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : (g.template_id ? <Check className="h-4 w-4" aria-hidden="true" /> : <Plus className="h-4 w-4" aria-hidden="true" />)}
                      {g.template_id ? t('orgPages:templates.fromPanel.linked') : t('orgPages:templates.fromPanel.create')}
                    </button>
                  </div>
                ))}
                {groups.length === 0 && <p className="py-3 text-center text-sm text-t4">{t('orgPages:templates.fromPanel.noGroups')}</p>}
              </div>
            );
          })()}
        </div>
      </DetailPanel>

      {/* Size template from an existing product (structure only) */}
      <DetailPanel
        open={fromSizeOpen}
        onClose={() => setFromSizeOpen(false)}
        title={t('orgPages:templates.sizeFromProduct.title')}
        footer={(
          <>
            <button type="button" onClick={() => setFromSizeOpen(false)} className={GHOST_BTN}>{t('common:cancel')}</button>
            <button type="button" onClick={createSizeFromProduct} disabled={!fromSizeProductId || fromSizeBusy} className={PRIMARY_BTN}>
              {fromSizeBusy ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : null}
              {t('common:create')}
            </button>
          </>
        )}
        testId="template-size-from-panel"
      >
        <div className="space-y-4">
          <p className="text-sm text-t3">
            {emphasizeAll(
              t('orgPages:templates.sizeFromProduct.note', {
                structure: t('orgPages:templates.sizeFromProduct.structure'),
                noPrices: t('orgPages:templates.sizeFromProduct.noPrices'),
              }),
              [t('orgPages:templates.sizeFromProduct.structure'), t('orgPages:templates.sizeFromProduct.noPrices')],
            )}
          </p>
          <SheetField label={t('orgPages:templates.fromPanel.product')} htmlFor={`${uid}-size-product`}>
            <select id={`${uid}-size-product`} value={fromSizeProductId} className={SELECT_CLS}
              onChange={e => {
                setFromSizeProductId(e.target.value);
                const p = products.find(x => String(x.id) === e.target.value);
                setFromSizeName(p ? t('orgPages:templates.sizeFromProduct.defaultName', { name: p.name }) : '');
              }}>
              <option value="">{t('orgPages:templates.fromPanel.pick')}</option>
              {products.filter(p => (p.sizes || []).length > 0).map(p => (
                <option key={p.id} value={p.id}>{t('orgPages:templates.sizeFromProduct.productSizes', { name: p.name, count: (p.sizes || []).length })}</option>
              ))}
            </select>
          </SheetField>
          {fromSizeProductId && (() => {
            const sizes = ((products.find(p => String(p.id) === fromSizeProductId) || {}).sizes) || [];
            return (
              <>
                <div className="flex flex-wrap gap-1.5">
                  {sizes.map((s: any) => <StatusChip key={s.id} tone="muted">{s.name}</StatusChip>)}
                </div>
                <SheetField label={t('orgPages:templates.sizeFromProduct.name')} htmlFor={`${uid}-size-from-name`}>
                  <input id={`${uid}-size-from-name`} value={fromSizeName} onChange={e => setFromSizeName(e.target.value)}
                    placeholder={t('orgPages:templates.sizeFromProduct.name')} className={INPUT_CLS} />
                </SheetField>
              </>
            );
          })()}
        </div>
      </DetailPanel>

      {/* ============================================================ */}
      {/* SIZE TEMPLATE FORM */}
      {/* ============================================================ */}
      <DetailPanel
        open={showSizeModal}
        onClose={() => setShowSizeModal(false)}
        width="lg"
        title={editingSizeTemplate ? t('admin:editSizeTemplateTitle') : t('admin:newSizeTemplateTitle')}
        footer={(
          <>
            <button type="button" onClick={() => setShowSizeModal(false)} className={GHOST_BTN}>{t('common:cancel')}</button>
            {saveBtn(handleSaveSizeTemplate, saving || !sizeForm.name.trim() || sizeForm.items.filter(i => i.name.trim()).length === 0, !!editingSizeTemplate)}
          </>
        )}
        testId="template-size-form"
      >
        <div className="xs-grid2">
          <SheetField label={`${t('admin:templateNameLabel')} *`} htmlFor={`${uid}-size-name`}>
            <input id={`${uid}-size-name`} type="text" value={sizeForm.name} className={INPUT_CLS}
              onChange={e => setSizeForm(f => ({ ...f, name: e.target.value }))} placeholder={t('admin:sizeTemplatePlaceholder')} />
          </SheetField>
          <SheetField label={t('admin:templateDescLabel')} htmlFor={`${uid}-size-desc`}>
            <input id={`${uid}-size-desc`} type="text" value={sizeForm.description} className={INPUT_CLS}
              onChange={e => setSizeForm(f => ({ ...f, description: e.target.value }))} />
          </SheetField>
        </div>

        <GroupTitle className="mt-6" aside={(
          <button type="button" className={LINK_BTN}
            onClick={() => setSizeForm(f => ({ ...f, items: [...f.items, { name: '', price_modifier: '0', is_default: false }] }))}>
            {t('admin:addSizeTemplateItem')}
          </button>
        )}>
          {t('admin:sizesLabel')}
        </GroupTitle>
        <div className="mt-2 divide-y divide-line border-y border-line">
          {sizeForm.items.map((item, idx) => (
            <div key={idx} className="flex flex-wrap items-center gap-2 py-2">
              <input
                type="text"
                value={item.name}
                aria-label={t('orgPages:templates.sizes.name', { index: idx + 1 })}
                onChange={e => {
                  const items = [...sizeForm.items];
                  items[idx] = { ...items[idx], name: e.target.value };
                  setSizeForm(f => ({ ...f, items }));
                }}
                className={`${COMPACT_INPUT} min-w-[8rem] flex-1`}
                placeholder={t('admin:sizeNamePlaceholder')}
              />
              <input
                type="number"
                step="0.01"
                min="0"
                aria-label={t('orgPages:templates.sizes.price', { index: idx + 1 })}
                value={item.price_modifier}
                onChange={e => {
                  const items = [...sizeForm.items];
                  items[idx] = { ...items[idx], price_modifier: e.target.value };
                  setSizeForm(f => ({ ...f, items }));
                }}
                className={`${COMPACT_INPUT} w-24 text-right tabular-nums`}
                placeholder="0.00"
              />
              <label className={defaultLabel}>
                <input
                  type="radio"
                  name={`${uid}-default-size`}
                  checked={item.is_default}
                  onChange={() => {
                    const items = sizeForm.items.map((it, i) => ({ ...it, is_default: i === idx }));
                    setSizeForm(f => ({ ...f, items }));
                  }}
                  className={checkCls}
                />
                {t('admin:defaultHeader')}
              </label>
              <button type="button" className={ROW_DANGER_BTN} aria-label={t('orgPages:templates.removeRow', { index: idx + 1 })}
                title={t('orgPages:templates.removeRow', { index: idx + 1 })}
                onClick={() => setSizeForm(f => ({ ...f, items: f.items.filter((_, i) => i !== idx) }))}>
                <X className="h-4 w-4" aria-hidden="true" />
              </button>
            </div>
          ))}
        </div>
      </DetailPanel>

      {/* ============================================================ */}
      {/* INGREDIENT TEMPLATE FORM */}
      {/* ============================================================ */}
      <DetailPanel
        open={showIngModal}
        onClose={() => setShowIngModal(false)}
        width="lg"
        title={editingIngTemplate ? t('admin:editIngTemplateTitle') : t('admin:newIngTemplateTitle')}
        footer={(
          <>
            <button type="button" onClick={() => setShowIngModal(false)} className={GHOST_BTN}>{t('common:cancel')}</button>
            {saveBtn(handleSaveIngTemplate, saving || !ingForm.name.trim() || ingForm.items.filter(i => i.ingredient_name.trim()).length === 0, !!editingIngTemplate)}
          </>
        )}
        testId="template-ing-form"
      >
        <div className="xs-grid2">
          <SheetField label={`${t('admin:templateNameLabel')} *`} htmlFor={`${uid}-ing-name`}>
            <input id={`${uid}-ing-name`} type="text" value={ingForm.name} className={INPUT_CLS}
              onChange={e => setIngForm(f => ({ ...f, name: e.target.value }))} placeholder={t('admin:ingTemplatePlaceholder')} />
          </SheetField>
          <SheetField label={t('admin:templateDescLabel')} htmlFor={`${uid}-ing-desc`}>
            <input id={`${uid}-ing-desc`} type="text" value={ingForm.description} className={INPUT_CLS}
              onChange={e => setIngForm(f => ({ ...f, description: e.target.value }))} />
          </SheetField>
        </div>

        <GroupTitle className="mt-6" aside={(
          <button type="button" className={LINK_BTN}
            onClick={() => setIngForm(f => ({ ...f, items: [...f.items, { ingredient_name: '', extra_price: '0', is_default: true, category: '' }] }))}>
            {t('admin:addSizeTemplateItem')}
          </button>
        )}>
          {t('admin:ingredientsLabel')}
        </GroupTitle>
        <div className="mt-2 divide-y divide-line border-y border-line">
          {ingForm.items.map((item, idx) => (
            <div key={idx} className="flex flex-wrap items-center gap-2 py-2">
              <input
                type="text"
                value={item.ingredient_name}
                aria-label={t('orgPages:templates.ingredients.name', { index: idx + 1 })}
                onChange={e => {
                  const items = [...ingForm.items];
                  items[idx] = { ...items[idx], ingredient_name: e.target.value };
                  setIngForm(f => ({ ...f, items }));
                }}
                className={`${COMPACT_INPUT} min-w-[8rem] flex-1`}
                placeholder={t('admin:ingredientPlaceholder')}
              />
              <input
                type="number"
                step="0.01"
                aria-label={t('orgPages:templates.ingredients.price', { index: idx + 1 })}
                value={item.extra_price}
                onChange={e => {
                  const items = [...ingForm.items];
                  items[idx] = { ...items[idx], extra_price: e.target.value };
                  setIngForm(f => ({ ...f, items }));
                }}
                className={`${COMPACT_INPUT} w-24 text-right tabular-nums`}
                placeholder="0.00"
              />
              <input
                type="text"
                value={item.category}
                aria-label={t('orgPages:templates.ingredients.category', { index: idx + 1 })}
                onChange={e => {
                  const items = [...ingForm.items];
                  items[idx] = { ...items[idx], category: e.target.value };
                  setIngForm(f => ({ ...f, items }));
                }}
                className={`${COMPACT_INPUT} w-28`}
                placeholder={t('admin:categoryPlaceholder')}
              />
              <label className={defaultLabel}>
                <input
                  type="checkbox"
                  checked={item.is_default}
                  onChange={e => {
                    const items = [...ingForm.items];
                    items[idx] = { ...items[idx], is_default: e.target.checked };
                    setIngForm(f => ({ ...f, items }));
                  }}
                  className={checkCls}
                />
                {t('admin:defaultHeader')}
              </label>
              <button type="button" className={ROW_DANGER_BTN} aria-label={t('orgPages:templates.removeRow', { index: idx + 1 })}
                title={t('orgPages:templates.removeRow', { index: idx + 1 })}
                onClick={() => setIngForm(f => ({ ...f, items: f.items.filter((_, i) => i !== idx) }))}>
                <X className="h-4 w-4" aria-hidden="true" />
              </button>
            </div>
          ))}
        </div>
      </DetailPanel>

      {/* ============================================================ */}
      {/* OPTION TEMPLATE FORM */}
      {/* ============================================================ */}
      <DetailPanel
        open={showOptModal}
        onClose={() => setShowOptModal(false)}
        width="lg"
        title={editingOptTemplate ? t('orgPages:templates.options.editTitle') : t('orgPages:templates.options.newTitle')}
        footer={(
          <>
            <button type="button" onClick={() => setShowOptModal(false)} className={GHOST_BTN}>{t('common:cancel')}</button>
            {saveBtn(handleSaveOptTemplate, saving || !optForm.name.trim() || optForm.choices.filter(c => c.name.trim()).length === 0, !!editingOptTemplate)}
          </>
        )}
        testId="template-opt-form"
      >
        <div className="xs-grid2">
          <SheetField label={`${t('admin:templateNameLabel')} *`} htmlFor={`${uid}-opt-name`}>
            <input id={`${uid}-opt-name`} type="text" value={optForm.name} className={INPUT_CLS}
              onChange={e => setOptForm(f => ({ ...f, name: e.target.value }))} placeholder={t('orgPages:templates.options.namePlaceholder')} />
          </SheetField>
          <SheetField label={t('admin:templateDescLabel')} htmlFor={`${uid}-opt-desc`}>
            <input id={`${uid}-opt-desc`} type="text" value={optForm.description} className={INPUT_CLS}
              onChange={e => setOptForm(f => ({ ...f, description: e.target.value }))} />
          </SheetField>
          <SheetField label={t('orgPages:templates.options.type')} htmlFor={`${uid}-opt-type`}>
            <select id={`${uid}-opt-type`} value={optForm.selection_type} className={SELECT_CLS}
              onChange={e => setOptForm(f => ({ ...f, selection_type: e.target.value as 'single' | 'multiple' }))}>
              <option value="single">{t('orgPages:templates.options.typeSingle')}</option>
              <option value="multiple">{t('orgPages:templates.options.typeMultiple')}</option>
            </select>
          </SheetField>
          <div className="flex items-end pb-2.5">
            <label className="inline-flex min-h-[44px] cursor-pointer select-none items-center gap-2.5 text-sm text-t2">
              <input type="checkbox" checked={optForm.is_required} className={checkCls}
                onChange={e => setOptForm(f => ({ ...f, is_required: e.target.checked }))} />
              {t('orgPages:templates.options.required')}
            </label>
          </div>
        </div>

        <GroupTitle className="mt-6" aside={(
          <button type="button" className={LINK_BTN}
            onClick={() => setOptForm(f => ({ ...f, choices: [...f.choices, { name: '', price_modifier: '0', is_default: false }] }))}>
            {t('orgPages:templates.options.addChoice')}
          </button>
        )}>
          {t('orgPages:templates.options.choicesLabel')}
        </GroupTitle>
        <div className="mt-2 divide-y divide-line border-y border-line">
          {optForm.choices.map((c, idx) => (
            <div key={idx} className="flex flex-wrap items-center gap-2 py-2">
              <input
                type="text"
                value={c.name}
                aria-label={t('orgPages:templates.options.choiceName', { index: idx + 1 })}
                onChange={e => updateOptChoice(idx, 'name', e.target.value)}
                className={`${COMPACT_INPUT} min-w-[8rem] flex-1`}
                placeholder={t('orgPages:templates.options.choicePlaceholder')}
              />
              <input
                type="number"
                step="0.01"
                aria-label={t('orgPages:templates.options.choicePrice', { index: idx + 1 })}
                value={c.price_modifier}
                onChange={e => updateOptChoice(idx, 'price_modifier', e.target.value)}
                className={`${COMPACT_INPUT} w-24 text-right tabular-nums`}
                placeholder="0.00"
              />
              <label className={defaultLabel}>
                <input type="checkbox" checked={c.is_default} className={checkCls}
                  onChange={e => updateOptChoice(idx, 'is_default', e.target.checked)} />
                {t('admin:defaultHeader')}
              </label>
              <button type="button" className={ROW_DANGER_BTN} aria-label={t('orgPages:templates.removeRow', { index: idx + 1 })}
                title={t('orgPages:templates.removeRow', { index: idx + 1 })}
                onClick={() => setOptForm(f => ({ ...f, choices: f.choices.filter((_, i) => i !== idx) }))}>
                <X className="h-4 w-4" aria-hidden="true" />
              </button>
            </div>
          ))}
        </div>
      </DetailPanel>

      {/* Ερωτήσεις πριν από διαγραφή / εφαρμογή στα συνδεδεμένα / μαζική δημιουργία (ως 14/10 window.confirm) */}
      <ConfirmModal
        isOpen={!!ask}
        variant={ask?.variant || 'danger'}
        title={ask?.title || ''}
        message={ask?.message || ''}
        confirmLabel={ask?.confirmLabel}
        cancelLabel={t('common:cancel')}
        onConfirm={() => { const a = ask; setAsk(null); a?.run(); }}
        onCancel={() => setAsk(null)}
      />
    </ListPage>
  );
};

export default OrgTemplatesManager;
